import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { mockApiInterceptor } from '@fuse/lib/mock-api';
import { fuseLoadingInterceptor } from '@fuse/services/loading';
import { provideTransloco,TranslocoService } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { authInterceptor } from 'app/core/auth/auth.interceptor';
import { idiomaInterceptor } from 'app/core/i18n/idioma.interceptor';
import { ErroresClienteHandler } from 'app/core/monitoring/errores-cliente.handler';
import { erroresHttpInterceptor } from 'app/core/monitoring/errores-http.interceptor';
import { accesoTandaInterceptor } from 'app/shared/services/acceso-tanda-eventos.service';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { esIdioma, idiomaGuardado, LanguageService, normalizarIdioma } from 'app/core/i18n/language.service';

import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { aparienciaGuardada, esquemaDe } from 'app/core/tema/tema.service';

export const appConfig: ApplicationConfig = {
    providers: [
        provideHttpClient(withInterceptors([
            // Primero: ve todas las fallas de la API (5xx/0) y las reporta al back.
            erroresHttpInterceptor,
            // 403/404 de la API: la tanda compartida abierta revisa si perdió el acceso.
            accesoTandaInterceptor,
            authInterceptor,
            idiomaInterceptor,
            fuseLoadingInterceptor,
            mockApiInterceptor
        ])),
        // Errores de JavaScript reportados al back (alarma de errores).
        { provide: ErrorHandler, useClass: ErroresClienteHandler },
        provideAnimations(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            // Meses, dias y primer dia de la semana del calendario en el idioma
            // de la interfaz (sin esto Material usa LOCALE_ID = en-US). Se lee
            // al abrir el primer datepicker, ya con el idioma aplicado; cambiar
            // de idioma con sesion recarga la app.
            provide: MAT_DATE_LOCALE,
            useFactory: () => inject(LanguageService).dateLocale(),
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    { id: 'es', label: 'Español' },
                    { id: 'en', label: 'English' },
                    { id: 'pt', label: 'Português' },
                    { id: 'fr', label: 'Français' },
                ],
                defaultLang: 'es',
                fallbackLang: 'es',
                missingHandler: { useFallbackTranslation: true, logMissingKey: false },
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                // El idioma elegido en este navegador (español si nunca eligio).
                // Se cargan los dos archivos: español es el respaldo de toda
                // llave que falte en ingles, portugues o frances.
                const translocoService = inject(TranslocoService);
                const language = inject(LanguageService);
                // `?lang=en|es|pt|fr` en la URL (la landing manda a
                // /auth/sign-up?lang=en) gana sobre lo guardado y queda como
                // eleccion hecha sin sesion, para subirla al perfil al entrar.
                let deUrl: string | null = null;
                try {
                    deUrl = normalizarIdioma(new URLSearchParams(window.location.search).get('lang'));
                } catch {}
                const lang = esIdioma(deUrl) ? deUrl : idiomaGuardado();
                language.usar(lang, esIdioma(deUrl) ? { sinSesion: true } : {});

                return () => firstValueFrom(
                    forkJoin([translocoService.load('es'), translocoService.load(lang)])
                );
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                // Clara salvo que se elija otra en Perfil (ver TemaService).
                scheme: esquemaDe(aparienciaGuardada()),
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
